import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import TourButton from '../../components/TourButton/TourButton';
import { getFoodsAndRecipesPageSteps } from '../../tours/foodsAndRecipesPage';
import axios from 'axios';
import { FormControl, MenuItem, Select as MuiSelect, useMediaQuery } from '@mui/material';
import { useAppContext } from '../../context/AppContext';
import { useNotification } from '../../components/notifications/useNotification';
import RecipeCard from '../../components/RecipeCard/RecipeCard';
import FoodModal from '../../components/Modals/FoodModal/FoodModal';
import RecipeModal from '../../components/Modals/RecipeModal/RecipeModal';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import './FoodsAndRecipes.css';
import { capitalizeFirst, sanitizeUrl } from '../../utils/functions';

/* ── Tab definitions ──────────────────────────────────────────────────── */
const TABS = [
    { key: 'all',     labelKey: 'far-tab-all'     },
    { key: 'recipes', labelKey: 'far-tab-recipes'  },
    { key: 'foods',   labelKey: 'far-tab-foods'    },
];

const CATEGORY_COLOR_MAP = {
    'poultry':       'orange',
    'meat':          'red',
    'fish & seafood':'blue',
    'dairy':         'blue',
    'eggs':          'orange',
    'grains':        'blue',
    'legumes':       'green',
    'vegetables':    'green',
    'fruits':        'purple',
    'healthy fats':  'green',
    'nuts & seeds':  'orange',
    'condiments':    'red',
    'beverages':     'blue',
    'supplements':   'orange',
    'other':         'blue',
};

const recipesLimitOptions = [4, 12, 24, 48, 96];
const foodsLimitOptions = [5, 10, 25, 50];

const muiLimitSx = {
    fontSize: '0.82rem',
    '&:before': { borderBottom: 'none' },
    '&:after': { borderBottom: 'none' },
    '& .MuiSelect-select': { paddingRight: '24px !important' },
};

const muiMenuItemSx = { fontSize: '0.82rem' };

/* ─────────────────────────────────────────────────────────────────────────
   FoodsAndRecipes component
───────────────────────────────────────────────────────────────────────── */
const FoodsAndRecipes = () => {
    const { t } = useTranslation();
    const { dietist, currentLanguage } = useAppContext();
    const { showSuccess, showError } = useNotification();
    const isMobile = useMediaQuery('(max-width: 768px)');

    /* ── State ────────────────────────────────────────────────────────── */
    const [activeTab, setActiveTab] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('recent');

    // Data
    const [recipes, setRecipes] = useState([]);
    const [loadingRecipes, setLoadingRecipes] = useState(false);
    const [foods, setFoods] = useState([]);
    const [loadingFoods, setLoadingFoods] = useState(false);

    // Pagination (for foods)
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [foodsPageSize, setFoodsPageSize] = useState(5);

    // Pagination (for recipes)
    const [recipePage, setRecipePage] = useState(1);
    const [recipeTotalPages, setRecipeTotalPages] = useState(1);
    const [recipeTotalCount, setRecipeTotalCount] = useState(0);
    const [recipesPageSize, setRecipesPageSize] = useState(4);

    // Food modal
    const [showFoodModal, setShowFoodModal] = useState(false);
    const [editFood, setEditFood] = useState(null);

    // Recipe modal
    const [showRecipeModal, setShowRecipeModal] = useState(false);
    const [editRecipe, setEditRecipe] = useState(null);
    const [deleteRecipe, setDeleteRecipe] = useState(null);

    // Food deletion
    const [deleteFood, setDeleteFood] = useState(null);

    /* ── Fetch foods ──────────────────────────────────────────────────── */
    const fetchFoods = async (page = 1, limit = foodsPageSize) => {
        if (!dietist?._id) return;
        try {
            setLoadingFoods(true);
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/foods`,
                { params: { page, limit }, withCredentials: true }
            );
            const data = res.data;
            setFoods(data.foods ?? data.data ?? []);
            setTotalCount(data.totalCount ?? data.total ?? 0);
            setTotalPages(data.totalPages ?? 1);
        } catch (error) {
            showError(t('far-error-fetch-foods'));
            console.error('fetchFoods error:', error);
        } finally {
            setLoadingFoods(false);
        }
    };

    /* ── Fetch recipes ─────────────────────────────────────────────────── */
    const fetchRecipes = async (page = 1, limit = recipesPageSize) => {
        if (!dietist?._id) return;
        try {
            setLoadingRecipes(true);
            const res = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/dietists/${dietist._id}/recipes`,
                { params: { page, limit }, withCredentials: true }
            );
            const data = res.data;
            setRecipes(data.recipes ?? data.data ?? data ?? []);
            setRecipeTotalCount(data.totalCount ?? data.total ?? 0);
            setRecipeTotalPages(data.totalPages ?? 1);
        } catch (error) {
            showError(t('far-error-fetch-recipes'));
            console.error('fetchRecipes error:', error);
        } finally {
            setLoadingRecipes(false);
        }
    };

    /* ── Initial data fetch ─────────────────────────────────────────── */
    useEffect(() => {
        fetchFoods(1);
        fetchRecipes();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── Derived data based on active tab ─────────────────────────────── */
    const showRecipes = activeTab === 'all' || activeTab === 'recipes';
    const showFoods   = activeTab === 'all' || activeTab === 'foods' || activeTab === 'protein';

    /* ── Handlers ─────────────────────────────────────────────────────── */
    const handleTabChange = (tabKey) => {
        setActiveTab(tabKey);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchFoods(page);
        }
    };

    const handleOpenAddFood = () => {
        setEditFood(null);
        setShowFoodModal(true);
    };

    const handleOpenEditFood = (food) => {
        setEditFood(food);
        setShowFoodModal(true);
    };

    const handleFoodSave = async () => {
        await fetchFoods(currentPage);
    };

    const handleOpenAddRecipe = () => {
        setEditRecipe(null);
        setShowRecipeModal(true);
    };

    const handleOpenEditRecipe = (recipe) => {
        setEditRecipe(recipe);
        setShowRecipeModal(true);
    };

    const handleRecipeSave = async () => {
        await fetchRecipes(recipePage, recipesPageSize);
    };

    const handleRecipePageChange = (page) => {
        if (page >= 1 && page <= recipeTotalPages) {
            setRecipePage(page);
            fetchRecipes(page, recipesPageSize);
        }
    };

    const handleRecipesPageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setRecipesPageSize(newSize);
        setRecipePage(1);
        fetchRecipes(1, newSize);
    };

    const handleFoodsPageSizeChange = (e) => {
        const newSize = Number(e.target.value);
        setFoodsPageSize(newSize);
        setCurrentPage(1);
        fetchFoods(1, newSize);
    };

    const handleDeleteRecipe = async () => {
        if (!deleteRecipe?._id) return;
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/recipes/${deleteRecipe._id}/`,
                { withCredentials: true }
            );
            showSuccess(t('far-success-delete-recipe'));
            await fetchRecipes();
            setDeleteRecipe(null);
        } catch (error) {
            showError(t('far-error-delete-recipe'));
            console.error('handleDeleteRecipe error:', error);
        }
    };

    const handleDeleteFood = async () => {
        if (!deleteFood?._id) return;
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/foods/${deleteFood._id}/`,
                { withCredentials: true }
            );
            showSuccess(t('far-success-delete-food'));
            await fetchFoods(currentPage);
            setDeleteFood(null);
        } catch (error) {
            showError(t('far-error-delete-food'));
            console.error('handleDeleteFood error:', error);
        }
    };

    /* ── Pagination numbers ───────────────────────────────────────────── */
    const renderPageButtons = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <button
                    key={i}
                    className={`far-page-btn ${currentPage === i ? 'active' : 'default'}`}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    /* ── Render ────────────────────────────────────────────────────────── */
    return (
        <>
        {showFoodModal && (
            <FoodModal
                setShowModal={setShowFoodModal}
                onSave={handleFoodSave}
                food={editFood}
            />
        )}
        {showRecipeModal && (
            <RecipeModal
                setShowModal={setShowRecipeModal}
                onSave={handleRecipeSave}
                recipe={editRecipe}
            />
        )}
        {deleteRecipe && (
            <ConfirmationModal
                titleText={`${t('far-delete-recipe')}: ${deleteRecipe.name?.[currentLanguage] ?? deleteRecipe.name?.en ?? ''}`}
                bodyText={t('far-confirm-delete-recipe')}
                handleConfirm={handleDeleteRecipe}
                handleClose={() => setDeleteRecipe(null)}
            />
        )}
        {deleteFood && (
            <ConfirmationModal
                titleText={`${t('far-delete-food')}: ${deleteFood.name?.[currentLanguage] ?? deleteFood.name?.en ?? ''}`}
                bodyText={t('far-confirm-delete-food')}
                handleConfirm={handleDeleteFood}
                handleClose={() => setDeleteFood(null)}
            />
        )}
        <div id="foods-and-recipes-page">
            <div className="far-container">

                {/* ── Top header ──────────────────────────────────── */}
                <div className="far-header-row" data-tour="far-header">
                    <div className="far-header-text">
                        <h2>{t('far-title')}</h2>
                        <p>{t('far-subtitle')}</p>
                    </div>
                    <div className="far-header-actions">
                        <div>
                            <TourButton steps={getFoodsAndRecipesPageSteps(t)} iconOnly={isMobile} />
                        </div>
                        <button className="far-btn far-btn-outline" onClick={handleOpenAddFood}>
                            <span className="material-symbols-outlined">add_circle</span>
                            {t('far-add-food')}
                        </button>
                        <button className="far-btn far-btn-primary" onClick={handleOpenAddRecipe}>
                            <span className="material-symbols-outlined">cooking</span>
                            {t('far-new-recipe')}
                        </button>
                    </div>
                </div>

                {/* ── Tabs ────────────────────────────────────────── */}
                <div className="far-tabs" data-tour="far-tabs">
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            className={`far-tab ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            {t(tab.labelKey)}
                        </button>
                    ))}
                </div>

                {/* ── Recipes section ─────────────────────────────── */}
                {showRecipes && (
                    <section data-tour="far-recipes">
                        <div className="far-section-header">
                            <h3>{activeTab === 'all' ? t('far-recent-recipes') : t('far-tab-recipes')}</h3>
                            {activeTab === 'recipes' && (
                                <div className='far-page-size-selector'>
                                <label>{capitalizeFirst(t('far-per-page'))}</label>
                                <FormControl variant="standard" size="small">
                                    <MuiSelect
                                        variant="standard"
                                        value={recipesPageSize.toString()}
                                        onChange={handleRecipesPageSizeChange}
                                        sx={muiLimitSx}
                                    >
                                        {recipesLimitOptions.map(n => (
                                            <MenuItem key={n} value={n.toString()} sx={muiMenuItemSx}>{n}</MenuItem>
                                        ))}
                                    </MuiSelect>
                                </FormControl>
                                </div>
                            )}
                        </div>

                        {loadingRecipes ? (
                            <div className="far-empty">
                                <span className="material-symbols-outlined">hourglass_empty</span>
                                <p>{t('far-loading-recipes')}</p>
                            </div>
                        ) : recipes.length === 0 ? (
                            <div className="far-empty">
                                <span className="material-symbols-outlined">restaurant</span>
                                <p>{t('far-no-recipes')}</p>
                            </div>
                        ) : (
                            <>
                                <div className="far-recipes-grid">
                                    {(activeTab === 'all' ? recipes.slice(0, 4) : recipes).map((r) => (
                                        <RecipeCard
                                            key={r._id}
                                            name={r.name?.[currentLanguage] ?? r.name?.en ?? ''}
                                            image={sanitizeUrl(r.imageUrl) ?? '/images/recipe_image_placeholder.jpg'}
                                            time={r.preparationTime ? `${r.preparationTime} min` : '—'}
                                            kcal={r.kcal ?? 0}
                                            servings={r.servings ?? 1}
                                            protein={r.proteins ?? 0}
                                            carbs={r.carbs ?? 0}
                                            fat={r.fats ?? 0}
                                            onClick={() => handleOpenEditRecipe(r)}
                                            onDelete={() => setDeleteRecipe(r)}
                                        />
                                    ))}
                                </div>
                                {activeTab === 'recipes' && recipeTotalPages > 1 && (
                                    <div className="far-table-footer">
                                        <span className="far-table-footer-info">
                                            {t('far-showing', {
                                                from: (recipePage - 1) * recipesPageSize + 1,
                                                to: Math.min(recipePage * recipesPageSize, recipeTotalCount),
                                                total: recipeTotalCount,
                                            })}
                                        </span>
                                        <div className="far-pagination">
                                            <button
                                                className="far-page-btn default"
                                                disabled={recipePage === 1}
                                                onClick={() => handleRecipePageChange(recipePage - 1)}
                                            >
                                                {t('far-prev')}
                                            </button>
                                            {Array.from({ length: recipeTotalPages }, (_, i) => i + 1).map((p) => (
                                                <button
                                                    key={p}
                                                    className={`far-page-btn ${recipePage === p ? 'active' : 'default'}`}
                                                    onClick={() => handleRecipePageChange(p)}
                                                >
                                                    {p}
                                                </button>
                                            ))}
                                            <button
                                                className="far-page-btn default"
                                                disabled={recipePage === recipeTotalPages}
                                                onClick={() => handleRecipePageChange(recipePage + 1)}
                                            >
                                                {t('far-next')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </section>
                )}

                {/* ── Food items table ────────────────────────────── */}
                {showFoods && (
                    <section className="far-table-section" data-tour="far-foods">
                        <div className="far-section-header">
                            <h3>{t('far-food-items')}</h3>
                        </div>

                        {loadingFoods ? (
                            <div className="far-empty">
                                <span className="material-symbols-outlined">hourglass_empty</span>
                                <p>{t('far-loading')}</p>
                            </div>
                        ) : foods.length === 0 ? (
                            <div className="far-empty">
                                <span className="material-symbols-outlined">lunch_dining</span>
                                <p>{t('far-no-foods')}</p>
                            </div>
                        ) : (
                            <div className="far-table-wrapper">
                                <table className="far-table">
                                    <thead>
                                        <tr>
                                            <th>{t('far-th-food')}</th>
                                            <th>{t('far-th-category')}</th>
                                            <th className="text-center">{t('unit')}</th>
                                            <th className="text-center">{t('kcal')}</th>
                                            <th className="text-center">{t('Proteins')} (gr)</th>
                                            <th className="text-center">{t('Carbs')} (gr)</th>
                                            <th className="text-center">{t('Fats')} (gr)</th>
                                            <th className="text-right">{t('far-th-actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {foods.map((f) => {
                                            const categoryStr = typeof f.category === 'object'
                                                ? (f.category?.[currentLanguage] ?? f.category?.en ?? '')
                                                : (f.category ?? '');
                                            const color = CATEGORY_COLOR_MAP[f.category?.en ?? f.category] ?? 'blue';
                                            const serving = f.grams ? `${f.grams} ${f.unit ?? 'g'}` : (f.unit ?? '—');
                                            const displayName = f.name?.[currentLanguage] ?? f.name?.en ?? f.name?.it ?? '';
                                            return (
                                            <tr key={f._id}>
                                                <td>
                                                    <div className="far-food-name-cell">
                                                        <div className={`far-food-icon ${color}`}>
                                                            <span className="material-symbols-outlined">restaurant</span>
                                                        </div>
                                                        <span className="far-food-name">{displayName}</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`far-category-badge ${color}`}>
                                                        {categoryStr}
                                                    </span>
                                                </td>
                                                <td className="text-center">{serving}</td>
                                                <td className="text-center bold">{f.kcal}</td>
                                                <td className="text-center primary">{f.proteins}</td>
                                                <td className="text-center">{f.carbs}</td>
                                                <td className="text-center">{f.fats}</td>
                                                <td className="text-right">
                                                    <button className="far-action-btn" title={t('far-edit')} onClick={() => handleOpenEditFood(f)}>
                                                        <span className="material-symbols-outlined">edit</span>
                                                    </button>
                                                    <button className="far-action-btn delete" title={t('far-delete')} onClick={() => setDeleteFood(f)}>
                                                        <span className="material-symbols-outlined">delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>

                                {/* Footer / pagination */}
                                <div className="far-table-footer">
                                    <span className="far-table-footer-info">
                                        {t('far-showing', {
                                            from: (currentPage - 1) * foodsPageSize + 1,
                                            to: Math.min(currentPage * foodsPageSize, totalCount),
                                            total: totalCount,
                                        })}
                                    </span>
                                    <div className="far-pagination">
                                        <FormControl variant="standard" size="small">
                                            <MuiSelect
                                                variant="standard"
                                                value={foodsPageSize.toString()}
                                                onChange={handleFoodsPageSizeChange}
                                                sx={muiLimitSx}
                                            >
                                                {foodsLimitOptions.map(n => (
                                                    <MenuItem key={n} value={n.toString()} sx={muiMenuItemSx}>{n}</MenuItem>
                                                ))}
                                            </MuiSelect>
                                        </FormControl>
                                        <button
                                            className="far-page-btn default"
                                            disabled={currentPage === 1}
                                            onClick={() => handlePageChange(currentPage - 1)}
                                        >
                                            {t('far-prev')}
                                        </button>
                                        {renderPageButtons()}
                                        <button
                                            className="far-page-btn default"
                                            disabled={currentPage === totalPages}
                                            onClick={() => handlePageChange(currentPage + 1)}
                                        >
                                            {t('far-next')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                )}

            </div>
        </div>
        </>
    );
};

export default FoodsAndRecipes;